import { useTranslation } from "react-i18next";

import { setLanguage } from "../i18n.js";
import { currentLanguage, type Language } from "../locale.js";
import { Flag } from "./Flag.js";

/**
 * The interface's language, chosen with a small flag in the corner. Asked for
 * by the owner on 2026-10-07; the decision is in `project-scope.md`.
 *
 * **The flag is the language it switches to, not the one in use.** A control
 * says what it does — the rule in `design-direction.md` — and a Swedish flag
 * on a Swedish screen would say *this is Swedish*, which the screen already
 * says in every word on it. So a Swedish reader sees the British flag, and the
 * name of the button, read aloud and shown on hover, is *In English* in the
 * language it offers, which is the one its reader is looking for.
 *
 * The same disc the wine list draws beside a country, because the owner asked
 * for that flag, and one size of flag in the interface reads as one element.
 * Two languages need one button rather than a menu; a third would make it a
 * menu.
 */
const OTHER: Readonly<Record<Language, { language: Language; flag: string }>> = {
  sv: { language: "en", flag: "GB" },
  en: { language: "sv", flag: "SE" },
};

export function LanguageSwitch() {
  const { t } = useTranslation();
  const next = OTHER[currentLanguage()];

  return (
    <button
      type="button"
      onClick={() => void setLanguage(next.language)}
      aria-label={t("language.switchTo")}
      title={t("language.switchTo")}
      lang={next.language}
      className="hover:text-accent self-center text-base leading-none opacity-80 transition-opacity hover:opacity-100"
    >
      <Flag countryCode={next.flag} />
    </button>
  );
}
